import { currentSchool } from './school';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues?: { path: string; message: string }[],
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type QueryParams = Record<string, string | number | boolean | null | undefined | string[]>;

export function buildQuery(params?: QueryParams) {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length) search.set(key, value.join(','));
    } else {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

async function request<T>(method: string, path: string, body?: unknown, query?: QueryParams): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  // The API works in the school named in this tab's URL.
  if (currentSchool) headers['x-edvora-school'] = currentSchool;

  const response = await fetch(`/api${path}${buildQuery(query)}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return readResponse<T>(response, path);
}

/** Sends a file as the raw request body, with its details in headers (e.g. facilities documents). */
export async function upload<T>(path: string, file: Blob, headers: Record<string, string> = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/octet-stream', ...(currentSchool ? { 'x-edvora-school': currentSchool } : {}), ...headers },
    body: file,
  });
  return readResponse<T>(response, path);
}

async function readResponse<T>(response: Response, path: string): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let data: { error?: string; issues?: { path: string; message: string }[]; details?: unknown } | undefined;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }

  if (!response.ok) {
    if (response.status === 401 && path !== '/auth/login') window.dispatchEvent(new Event('ec:unauthorized'));
    // The school's plan lapsed since the page loaded: reload who's signed in so the app shows it.
    if (response.status === 402 && (data?.details as { code?: string } | undefined)?.code === 'SUBSCRIPTION_REQUIRED') {
      window.dispatchEvent(new Event('ec:plan-changed'));
    }
    const issues = data?.issues;
    const message = issues?.length
      ? `${data?.error ?? 'Some fields are invalid'}: ${issues.map((i) => (i.path ? `${i.path} - ${i.message}` : i.message)).join('; ')}`
      : (data?.error ?? `Request failed (${response.status})`);
    throw new ApiError(message, response.status, issues, data?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: QueryParams) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown = {}) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown = {}) => request<T>('PATCH', path, body),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
};

/** For links the browser opens itself (CSV downloads). Those can't send headers, so the school goes in the query. */
export const apiUrl = (path: string, query?: QueryParams) => `/api${path}${buildQuery({ ...query, school: currentSchool })}`;

/** An /api address the server handed out (such as a picture's), with the school added so the browser can load it itself. */
export const apiFileUrl = (url: string) => (currentSchool ? `${url}${url.includes('?') ? '&' : '?'}school=${encodeURIComponent(currentSchool)}` : url);

export function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Something went wrong';
}
